import React, {useMemo, useState} from 'react';
import ChevronDown from "@mui/icons-material/ExpandMoreRounded";
import ChevronRight from "@mui/icons-material/ChevronRightRounded";
import Lightning from "@mui/icons-material/BoltRounded";
import Cpu from "@mui/icons-material/MemoryRounded";
import FileText from "@mui/icons-material/DescriptionRounded";
import {ChargerTypes} from '../../../configuration/constants/evChargerConstants';
import '../../styles/statsSidebar.css';

const SidebarItem = ({label, icon: Icon, isActive, onClick, className = '', isLeaf = false, index}) => (
  <button
    type="button"
    className={`sidebar-item ${isActive ? 'active' : ''} ${isLeaf ? 'leaf-item' : ''} ${className}`}
    onClick={onClick}
  >
    {Icon && <Icon className="category-icon" size={16}/>}
    {isLeaf && index !== undefined && <span className="item-number">{index + 1}.</span>}
    <span className="item-label">{label}</span>
  </button>
);

const SidebarSection = ({title, icon: Icon, isOpen, onToggle, count, children}) => (
  <div className="sidebar-section">
    <button
      type="button"
      className={`sidebar-item category-header ${isOpen ? 'open' : ''}`}
      onClick={onToggle}
    >
      <div className="category-content">
        <Icon className="category-icon" size={18}/>
        <span className="item-label">{title} ({count})</span>
      </div>
      <span className="expand-icon">
        {isOpen ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
      </span>
    </button>
    {isOpen && (
      <div className="sidebar-subsection">
        {children}
      </div>
    )}
  </div>
);

const DeviceTreeContent = ({chartsConfig, activeTab, onTabChange}) => {
  const [expandedCategories, setExpandedCategories] = useState({"ev-chargers": true, "devices": true});
  const chargerTypeValues = new Set(Object.values(ChargerTypes));

  const organizedData = useMemo(() => {
    if (!chartsConfig?.groups) return {evChargers: [], devices: []};

    const evChargers = [];
    const devices = [];

    chartsConfig.groups.forEach(group => {
      const isEVCharger = chargerTypeValues.has(group.chargerType);

      if (isEVCharger) {
        evChargers.push(group);
      } else {
        devices.push(group);
      }
    });

    return {evChargers, devices};
  }, [chartsConfig]);

  const toggleCategory = (key) => {
    setExpandedCategories(prev => ({...prev, [key]: !prev[key]}));
  };

  return (
    <div className="device-tree-content h-100 d-flex flex-column">
      <div className="sidebar-tree-content flex-grow-1 overflow-auto">
        {/* EV Chargers Section */}
        {organizedData.evChargers.length > 0 && (
          <SidebarSection
            title="EV Chargers"
            icon={Lightning}
            isOpen={expandedCategories['ev-chargers']}
            onToggle={() => toggleCategory('ev-chargers')}
            count={organizedData.evChargers.length}
          >
            {organizedData.evChargers.map((ev, idx) => (
              <SidebarItem
                key={`ev-${ev.name}`}
                label={ev.name}
                isActive={activeTab === ev.name}
                onClick={() => onTabChange(ev.name)}
                isLeaf
                index={idx}
              />
            ))}
          </SidebarSection>
        )}

        {/* Devices Section */}
        {organizedData.devices.length > 0 && (
          <SidebarSection
            title="Devices"
            icon={Cpu}
            isOpen={expandedCategories['devices']}
            onToggle={() => toggleCategory('devices')}
            count={organizedData.devices.length}
          >
            {organizedData.devices.map((device, idx) => (
              <SidebarItem
                key={`dev-${device.name}`}
                label={device.name}
                isActive={activeTab === device.name}
                onClick={() => onTabChange(device.name)}
                isLeaf
                index={idx}
              />
            ))}
          </SidebarSection>
        )}

        {/* Logs Section */}
        <div className="sidebar-section">
          <SidebarItem
            label="Logs"
            icon={FileText}
            isLeaf
            isActive={activeTab === 'logs'}
            onClick={() => onTabChange('logs')}
            className="gap-2 align-items-center"
          />
        </div>
      </div>
    </div>
  );
};

export default DeviceTreeContent;


